import { analyzeImageQuality, boxesFromDetections } from "./imageQuality";
import { patLogs } from "../context/utils";

/**
 * Continuous low-light detection -> camera torch control.
 *
 * Runs analyzeImageQuality on a throttled stream of frames and keeps a rolling
 * window of the last 5 results, deciding on the frame's mean brightness
 * (overallBrightness, 0-255):
 *   - all 5 frames below TORCH_ON_BRIGHTNESS  -> torch ON
 *   - all 5 frames above TORCH_OFF_BRIGHTNESS -> torch OFF, but only once it has
 *                                               been on for TORCH_MIN_ON_MS
 *   - anything in between                     -> leave the torch alone
 *
 * TORCH_MIN_ON_MS is a MINIMUM on-time, not an auto-off: nothing switches the
 * torch off on a timer. It exists because the torch lights the very scene being
 * measured, so switching on lifts the reading above the OFF cut within a frame or
 * two -- without a floor the torch would turn itself straight back off. A bright
 * reading inside that window is ignored; past it, a bright reading ends the
 * on-period. While the scene stays dark the torch stays on.
 *
 * The gap between the two cuts is the other half of that: the OFF cut sits above
 * whatever the torch itself contributes to the reading.
 *
 * Everything is recorded so the UI can show the 5-frame data, the verdict and
 * the ON/OFF events. Never throws: camera work is best-effort.
 */

const WINDOW_SIZE = 5;
// Shortest time the torch stays on. A bright window inside it is ignored, so the
// torch's own contribution to the reading cannot switch it off immediately.
const TORCH_MIN_ON_MS = 10000;
// Cuts on mean frame luma (overallBrightness, 0-255). Tied to
// analyzeImageQuality's brightness scale, not to its lowLight / overallLowLight
// flags — those are still measured and logged, but no longer drive the torch.
// All WINDOW_SIZE frames must clear a cut for it to act.
//
// Both cuts are tunable live from the camera view (see the +/- strip fed by
// adjustTorchThreshold below); these are the values they start at and the ones
// resetTorchThresholds returns to. Tuning is deliberately NOT cleared by
// resetTorch, so a value dialled in during testing survives a camera restart.
const DEFAULT_TORCH_ON_BRIGHTNESS = 60;
// Raised from the original 80 so the torch's own light does not trip it: with the
// torch on, a dark scene reads well above 80 but stays under 100, so only a
// genuinely bright scene switches it off early.
//
// The 40-point gap to the ON cut is the whole reason there are two numbers. A
// single cut at 60 would sit right where the torch parks the reading, and the
// lamp would switch itself off and straight back on for as long as the user
// held still.
const DEFAULT_TORCH_OFF_BRIGHTNESS = 100;
let TORCH_ON_BRIGHTNESS = DEFAULT_TORCH_ON_BRIGHTNESS;
let TORCH_OFF_BRIGHTNESS = DEFAULT_TORCH_OFF_BRIGHTNESS;
// Minimum gap between two analyses. Note the check is driven by the detection
// loop, so the real cadence is also bounded by minDetectIntervalMs (~250ms):
// with 300 here a frame is analyzed roughly every 500ms.
const MIN_INTERVAL_MS = 300;

/*
 * How much of the reading the lamp is producing itself.
 *
 * Without this the torch hunts, and the hunt is not subtle -- the lamp blinks
 * on and off every ten seconds for as long as the user stands still. A dim room
 * reads 45, under the ON cut, so the lamp fires; the lamp lifts the same room to
 * 115, over the OFF cut, so once the minimum on-time expires the lamp is
 * released; the room reads 45 again. Both decisions are correct and together
 * they are a loop, because the second one is judging light the first one
 * created.
 *
 * So measure it. The jump in the reading across switch-on is what the lamp
 * contributes on THIS device in THIS scene -- no constant would do, since it
 * depends on the phone, the distance to the car and the colour of the paint --
 * and the release then tests the room rather than the room plus the lamp.
 *
 * Measured once per on-period, a moment after switching, so the sensor's own
 * auto-exposure has stopped reacting. Until then it is null and the reading is
 * used as-is, which is the old behaviour and cannot release early anyway: the
 * minimum on-time is far longer than the settle.
 */
const TORCH_GAIN_SETTLE_MS = 1500;
let torchGain = null; // luma the lamp adds, null until measured
let torchPreOn = null; // last reading taken with the lamp off
let torchGainAt = 0; // epoch ms the measurement may be taken

/*
 * Inspection this camera belongs to, for the step log. Held rather than read
 * from the ambient one in context/utils: that is only set on a retake, so it is
 * empty for an ordinary run and the log would have nothing to attach to.
 */
let logInspectionId = "";

/*
 * "This device has no lamp" is a fact about the hardware, not an event. The
 * probe runs repeatedly -- the control polls it, and every new camera re-runs
 * it -- so it is written once per session and then held.
 */
let unsupportedLogged = false;

/*
 * Whether this client has the flash feature at all, from config["torch"].
 *
 * Off unless the config says otherwise. The feature touches the camera, the
 * pictures and the screen, so a client that has not asked for it gets nothing:
 * no analysis, no lamp, no button, and no capability probe either -- a client
 * with the feature off should not be reporting whether its hardware has a lamp.
 */
let torchEnabled = false;

/**
 * One step-log line per flash event, in the agreed shape:
 *
 *   flashlight.<mode>.<trigger>     feature.mode.trigger
 *
 * mode says who decided -- the brightness window (auto) or the user (manual) --
 * and trigger is on or off.
 *
 * The two modes are recorded on different terms, deliberately:
 *
 *   auto    only when the lamp actually changes. Nobody asked for it, so the
 *           only event there is to report is the lamp itself.
 *
 *   manual  whenever the user selects on or off, whether or not the lamp
 *           changes. Turning the flash off when it happens to be off already is
 *           still the user turning the flash off, and it is the thing you want
 *           to see in the log when someone says the flash was in their way.
 *
 * Exactly one line per event either way: the auto path is skipped while a manual
 * mode is active, and the manual path is the only writer in that mode.
 *
 * Never awaited and never allowed to throw. This runs inside the frame path,
 * where a logging call that can block or reject would cost frames.
 */
const logFlashEvent = (mode, trigger) => {
  try {
    if (!logInspectionId) return; // nothing to attach the line to
    Promise.resolve(
      patLogs(`flashlight.${mode}.${trigger}`, logInspectionId),
    ).catch(() => {});
  } catch (e) {
    /* logging only */
  }
};

let track = null;
let frames = []; // last WINDOW_SIZE analyses
let events = []; // torch ON/OFF log
let torchOn = false;
let torchSupported = null; // null = unknown yet
let torchOnSince = 0; // epoch ms the torch was switched on, 0 when off
let busy = false;
let lastRun = 0;

/**
 * Who is driving the lamp.
 *
 *   "auto"  the window vote below decides, as it always has.
 *   "on"    forced on by the user; the vote is ignored.
 *   "off"   forced off by the user; the vote is ignored.
 *
 * Frames are still analysed and logged in the manual modes -- the panel and the
 * sheet stay meaningful, and switching back to auto has a warm window to work
 * with rather than starting blind.
 */
export const TORCH_MODES = { AUTO: "auto", ON: "on", OFF: "off" };
let torchMode = TORCH_MODES.AUTO;

const now = () => Date.now();
const hhmmss = () => new Date().toTimeString().slice(0, 8);

const pushEvent = (msg) => {
  events.push(`${hhmmss()} ${msg}`);
  if (events.length > 20) events.shift();
};

/**
 * Give the controller the live camera track (from stream.getTracks()[0]), and
 * the inspection its lamp events belong to.
 */
export const setTorchTrack = (t, inspectionId, enabled) => {
  torchEnabled = !!enabled;
  track = t || null;
  logInspectionId = inspectionId || "";
  torchSupported = null;
  // Nothing to probe, nothing to drive.
  if (!torchEnabled) return;
  // Probe straight away rather than waiting for the first switch attempt. In
  // auto mode on a bright scene nothing ever tries the lamp, so a lazy check
  // would leave support unknown -- and the control on screen -- indefinitely on
  // a device that has no flash at all.
  probeTorchSupport();
};

/** Reset everything (new session). */
export const resetTorch = () => {
  frames = [];
  events = [];
  busy = false;
  lastRun = 0;
  torchOnSince = 0;
  torchOn = false;
  torchGain = null;
  torchPreOn = null;
  torchGainAt = 0;
  unsupportedLogged = false;
  // Back to auto for the new session. Leaving a forced mode here would strand the
  // control claiming ON while the fresh track's lamp is actually dark, since this
  // clears torchOn without touching any camera.
  torchMode = TORCH_MODES.AUTO;
};

/**
 * Live tuning of the two brightness cuts, driven by the +/- strip in the camera
 * view. Both are plain numbers on analyzeImageQuality's 0-255 brightness scale.
 *
 * The ON cut must stay strictly below the OFF cut: if they crossed, a frame
 * between them would count as both dark and bright at once and the window vote
 * would be meaningless. Every setter clamps to keep MIN_CUT_GAP between them, so
 * pushing one cut into the other shoves that one along rather than inverting the
 * pair. Values are clamped to the scale itself too.
 */
const MIN_CUT_GAP = 1;
const clampCut = (v) => Math.min(255, Math.max(0, Math.round(v)));

/** Current cuts plus the values resetTorchThresholds restores. */
export const getTorchThresholds = () => ({
  on: TORCH_ON_BRIGHTNESS,
  off: TORCH_OFF_BRIGHTNESS,
  defaultOn: DEFAULT_TORCH_ON_BRIGHTNESS,
  defaultOff: DEFAULT_TORCH_OFF_BRIGHTNESS,
});

/**
 * Set both cuts at once. `off` wins any conflict: `on` is pulled down to sit
 * MIN_CUT_GAP below it. Returns the values actually applied.
 */
export const setTorchThresholds = (on, off) => {
  try {
    // Floored at MIN_CUT_GAP, not 0: the ON cut is derived as nextOff - gap, so
    // an OFF cut of 0 would put ON below the scale and the torch could never
    // switch on again. Driving OFF to the floor parks the pair at 0 / 1.
    const nextOff = Math.max(
      MIN_CUT_GAP,
      clampCut(
        off === undefined || off === null || isNaN(off)
          ? TORCH_OFF_BRIGHTNESS
          : off,
      ),
    );
    const wantOn = clampCut(
      on === undefined || on === null || isNaN(on) ? TORCH_ON_BRIGHTNESS : on,
    );
    const nextOn = Math.min(wantOn, nextOff - MIN_CUT_GAP);
    if (nextOn !== TORCH_ON_BRIGHTNESS || nextOff !== TORCH_OFF_BRIGHTNESS) {
      pushEvent(`cuts ${TORCH_ON_BRIGHTNESS}/${TORCH_OFF_BRIGHTNESS} -> ${nextOn}/${nextOff}`);
    }
    TORCH_ON_BRIGHTNESS = nextOn;
    TORCH_OFF_BRIGHTNESS = nextOff;
  } catch (e) {
    /* tuning only */
  }
  return getTorchThresholds();
};

/**
 * Nudge one cut by `delta`. `which` is "on" or "off". Whichever cut is moved,
 * the other is pushed out of the way rather than crossed. Returns the applied
 * values so the caller can re-render from the truth rather than its own guess.
 */
export const adjustTorchThreshold = (which, delta) => {
  try {
    const d = isNaN(delta) ? 0 : Number(delta);
    if (which === "off") {
      const nextOff = clampCut(TORCH_OFF_BRIGHTNESS + d);
      // Lowering OFF into ON drags ON down with it.
      return setTorchThresholds(
        Math.min(TORCH_ON_BRIGHTNESS, nextOff - MIN_CUT_GAP),
        nextOff,
      );
    }
    const nextOn = clampCut(TORCH_ON_BRIGHTNESS + d);
    // Raising ON into OFF pushes OFF up with it.
    return setTorchThresholds(
      nextOn,
      Math.max(TORCH_OFF_BRIGHTNESS, nextOn + MIN_CUT_GAP),
    );
  } catch (e) {
    return getTorchThresholds();
  }
};

/** Back to the shipped 60 / 100. */
export const resetTorchThresholds = () =>
  setTorchThresholds(DEFAULT_TORCH_ON_BRIGHTNESS, DEFAULT_TORCH_OFF_BRIGHTNESS);

/**
 * True when checkFrameForTorch would actually analyze the next frame — the
 * throttle window has elapsed and nothing is in flight. Callers use this so a
 * frame capture taken purely for the torch check is only paid for when the
 * check will really consume it.
 */
export const shouldAnalyzeFrame = () =>
  torchEnabled && !busy && now() - lastRun >= MIN_INTERVAL_MS;

/**
 * Ask the track whether it has a lamp. Returns true / false / null, where null
 * means "cannot tell yet".
 *
 * That third answer matters. A track often publishes no capabilities at all for
 * the first moments after getUserMedia, and reading an empty object as "no
 * torch" would hide the control on hardware that does have one. So an empty set
 * leaves the answer unknown and the caller is expected to ask again; only a
 * populated set without a torch key is a real no.
 */
/**
 * Settle on "no lamp" and say so, once.
 *
 * Worth a line of its own because the alternative is silence: with no lamp the
 * control is hidden and neither an auto nor a manual line can ever be written,
 * so a session on a device without a flash reads exactly like a session that
 * never needed one.
 *
 * The once-flag is only set when a line was actually written. The probe can run
 * before the camera hands over its inspection id, and setting the flag on that
 * pass would suppress the real line for the rest of the session.
 */
const noteTorchUnsupported = (why) => {
  if (torchSupported !== false) pushEvent(`torch NOT supported (${why})`);
  torchSupported = false;
  if (unsupportedLogged || !logInspectionId) return;
  unsupportedLogged = true;
  logFlashEvent(
    torchMode === TORCH_MODES.AUTO ? "auto" : "manual",
    "unavailable",
  );
};

const probeTorchSupport = () => {
  try {
    if (!track || typeof track.getCapabilities !== "function") {
      // No track, or a browser without the capabilities API: there is no way to
      // reach a lamp from here, so this is a definite no rather than unknown.
      noteTorchUnsupported("no track or no capabilities API");
      return false;
    }
    const caps = track.getCapabilities() || {};
    if (Object.keys(caps).length === 0) {
      // Not published yet. Unknown, not absent -- deciding here would hide the
      // control on hardware that does have a lamp.
      torchSupported = null;
      return null;
    }
    if (!caps.torch) {
      noteTorchUnsupported("camera reports no torch");
      return false;
    }
    torchSupported = true;
    return true;
  } catch (e) {
    noteTorchUnsupported("capability read threw");
    return false;
  }
};

/** Public probe, so the UI can settle the question without touching the lamp. */
export const refreshTorchSupport = () =>
  torchEnabled ? probeTorchSupport() : false;

const torchCapable = () => {
  if (torchSupported === null) probeTorchSupport();
  return torchSupported === true;
};

const applyTorch = async (on) => {
  try {
    if (!torchCapable()) return false;
    await track.applyConstraints({ advanced: [{ torch: on }] });
    return true;
  } catch (e) {
    pushEvent(`torch ${on ? "ON" : "OFF"} failed`);
    return false;
  }
};

const switchTorchOn = async (reason) => {
  if (torchOn) return;
  const ok = await applyTorch(true);
  if (!ok) return;
  torchOn = true;
  // Start of the minimum on-time. Nothing is scheduled: the off decision is taken
  // by the frame checks, which is why a still-dark scene keeps the torch on
  // rather than losing it to a timer.
  torchOnSince = Date.now();
  // A fresh on-period gets a fresh measurement: the room is not the room it was
  // the last time the lamp fired.
  torchGain = null;
  torchGainAt = Date.now() + TORCH_GAIN_SETTLE_MS;
  pushEvent(`TORCH ON (${reason})`);
  // Auto only: in a manual mode setTorchMode reports the user's action instead,
  // and logging here as well would write the same event twice.
  if (torchMode === TORCH_MODES.AUTO) logFlashEvent("auto", "on");
};

/** How long the torch has been on, in ms. 0 when it is off. */
const torchOnFor = () =>
  torchOn && torchOnSince ? Date.now() - torchOnSince : 0;

/** Switch off ahead of the auto-off timer, cancelling it. */
const switchTorchOff = async (reason) => {
  if (!torchOn) return;
  const ok = await applyTorch(false);
  if (!ok) return;
  torchOn = false;
  torchOnSince = 0;
  torchGain = null;
  torchGainAt = 0;
  pushEvent(`TORCH OFF (${reason})`);
  // Auto only -- see switchTorchOn.
  if (torchMode === TORCH_MODES.AUTO) logFlashEvent("auto", "off");
  // Make coming back on earn a fresh window of dark frames.
  frames = [];
};

/**
 * Analyze one frame and update the torch decision.
 *
 * @param base64      frame from captureImgFrame()
 * @param detections  current detections (normalized bboxes) — used to crop the car
 * @param videoW/videoH  natural size of the captured frame
 * @param region      guidance region this frame belongs to, recorded on the sheet
 *                    row so a row can be traced back to the part of the car it
 *                    came from
 */
export const checkFrameForTorch = async (
  base64,
  detections,
  videoW,
  videoH,
  region,
) => {
  try {
    if (!torchEnabled) return;
    if (!base64 || !videoW || !videoH) return;
    if (busy) return; // don't stack analyses
    const t = now();
    if (t - lastRun < MIN_INTERVAL_MS) return;
    lastRun = t;
    busy = true;

    // Union of the detected part boxes, in pixels, plus the lamps to leave out of
    // the glare score. With no detections this is the whole frame, so the check
    // keeps running continuously.
    const boxes = boxesFromDetections(detections, videoW, videoH);

    const q = await analyzeImageQuality(base64, boxes.car, {
      headlightBoxes: boxes.headlightBoxes,
      taillightBoxes: boxes.taillightBoxes,
    });

    // The frame's own mean brightness drives the switch. A NaN (never expected
    // for the whole-frame pass) fails both tests and simply holds the state.
    const brightness = q.overallBrightness;

    if (!torchOn) {
      // The last thing seen without the lamp, kept so switch-on has something to
      // be measured against.
      torchPreOn = brightness;
    } else if (torchGain === null && torchGainAt && now() >= torchGainAt) {
      torchGain = torchPreOn === null ? 0 : Math.max(0, brightness - torchPreOn);
      pushEvent(`torch adds ~${torchGain.toFixed(0)} luma`);
    }

    /*
     * What the room would read with the lamp off. Equal to the raw reading
     * whenever the lamp is off or its contribution is not known yet, so this
     * only ever makes the release harder, never easier.
     */
    const roomBrightness =
      torchOn && torchGain !== null ? brightness - torchGain : brightness;

    const isLow = brightness < TORCH_ON_BRIGHTNESS;
    // Deliberately the room, not the frame: releasing on the frame is releasing
    // on the lamp's own light.
    const isHigh = roomBrightness > TORCH_OFF_BRIGHTNESS;

    frames.push({
      low: isLow,
      high: isHigh,
      b: q.avgBrightness,
      ob: brightness,
      room: Number(roomBrightness.toFixed(1)),
      ab: q.ambientBrightness,
      dp: q.darkPixelPercent,
      t: hhmmss(),
    });
    if (frames.length > WINDOW_SIZE) frames.shift();

    // Decide only on a full window of 5. Either cut needs the whole window to
    // agree, so a single stray frame cannot flip the torch.
    //
    //   dark   (all 5 < ON cut)                   -> on
    //   bright (all 5 > OFF cut) and torch on:
    //       on for less than TORCH_MIN_ON_MS      -> nothing, stay on
    //       on for at least  TORCH_MIN_ON_MS      -> off
    //   bright and torch already off              -> nothing
    //   anything between the cuts                 -> hold
    let lowCount;
    if (frames.length === WINDOW_SIZE) {
      lowCount = frames.filter((f) => f.low).length;
      const highCount = frames.filter((f) => f.high).length;
      const onForMs = torchOnFor();

      // Manual modes: keep measuring, but never touch the lamp -- the user's
      // choice outranks the vote until they hand control back.
      if (torchMode !== TORCH_MODES.AUTO) {
        // nothing to do; the frames above still feed the window
      } else if (lowCount === WINDOW_SIZE) {
        // Dark on all 5 frames. No-ops when the torch is already on, so a scene
        // that stays dark simply keeps it on.
        await switchTorchOn(
          `${WINDOW_SIZE}/${WINDOW_SIZE} <${TORCH_ON_BRIGHTNESS}`,
        );
      } else if (highCount === WINDOW_SIZE && torchOn) {
        // Bright on all 5 frames, and the torch is doing the lighting. Only end
        // the on-period once the minimum has elapsed; before that this is the
        // torch reading its own light back, so ignore it.
        if (onForMs >= TORCH_MIN_ON_MS) {
          await switchTorchOff(
            `${WINDOW_SIZE}/${WINDOW_SIZE} >${TORCH_OFF_BRIGHTNESS} after ` +
              `${(onForMs / 1000).toFixed(1)}s`,
          );
        }
      }
      // Bright with the torch already off, or anything between the two cuts:
      // nothing to do.
    }

  } catch (e) {
    // never break the detection loop
  } finally {
    busy = false;
  }
};

export const isTorchOn = () => torchOn;

/** Current mode: "auto" | "on" | "off". */
export const getTorchMode = () => torchMode;

/**
 * Is the lamp usable at all? null until the first capability read, so the UI can
 * render neutrally rather than claiming "unsupported" before it knows.
 */
export const isTorchSupported = () => torchSupported;

/**
 * Everything the control needs in one read, so it never has to assemble state
 * from several calls that could disagree mid-render.
 */
export const getTorchState = () => ({
  mode: torchMode,
  on: torchOn,
  supported: torchSupported,
  // Distinct from `supported` on purpose: "this client does not have the
  // feature" and "this phone has no lamp" are different answers, and collapsing
  // them would make the second impossible to diagnose.
  enabled: torchEnabled,
});

/**
 * Hand the lamp to the user, or back to the window vote.
 *
 * Switching to "on"/"off" acts immediately rather than waiting for the next
 * window, because a control that takes up to a few seconds to visibly do
 * anything reads as broken. Switching to "auto" deliberately leaves the lamp
 * exactly as it is: the next full window decides, and forcing a state here would
 * flick the light for no reason the user could explain.
 */
export const setTorchMode = async (next) => {
  try {
    if (!torchEnabled) return getTorchState();
    const wanted = Object.values(TORCH_MODES).includes(next)
      ? next
      : TORCH_MODES.AUTO;
    if (wanted === torchMode) return getTorchState();
    const from = torchMode;
    torchMode = wanted;
    pushEvent(`mode ${from} -> ${wanted}`);
    /*
     * Report the user's choice, not the lamp's transition -- so selecting off
     * when the lamp is already off is still recorded. Checked against the state
     * afterwards rather than announced up front, so a switch the camera refused
     * is not logged as though it happened.
     */
    if (wanted === TORCH_MODES.ON) {
      await switchTorchOn("manual");
      if (torchOn) logFlashEvent("manual", "on");
    } else if (wanted === TORCH_MODES.OFF) {
      await switchTorchOff("manual");
      if (!torchOn) logFlashEvent("manual", "off");
    }
  } catch (e) {
    /* camera work is best-effort */
  }
  return getTorchState();
};
